import { useEffect, useMemo, useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import {
  createExternalLink,
  reinstateExternalLink,
  updateExternalLink,
  type ExternalLinkListItem,
  type ExternalLinkPermission,
} from "./externalLinks.api";
import type { CreateExternalLinkRequest } from "@ServerTypes/externalLinks/externalLinks.schema";
import {
  copyToClipboard,
  endOfDayTimestamp,
  LINK_DIALOG_WIDTH,
  LINK_NAME_MAX_LENGTH,
  LINK_PASSWORD_MAX_LENGTH,
  LINK_PASSWORD_MIN_LENGTH,
  formatShortDate,
  isPastExpiry,
  minExpiryDateInput,
  timestampToDateInput,
} from "./externalLinks.helpers";

/**
 * Create / edit an external link, then show the resulting URL.
 *
 * Two stages in one dialog, matching the product prototype: the form, then a confirmation panel
 * carrying the copyable URL and a summary. Editing an existing link reuses the same form and lands
 * on the same confirmation — the URL never changes on edit, which is the point worth showing.
 */

/** How long the copy button shows its confirmation before reverting (PRD 3.3). */
const COPY_CONFIRM_MS = 1500;

/**
 * The item a link is being created on.
 *
 * The three ids/enums are taken straight from the server's create-request type rather than declared
 * as `string`, so validation happens once at the true origin (`toExternalLinkTarget` in
 * useFileManager, where a grid row is converted) instead of being papered over with a cast here.
 */
type ExternalLinkTarget = Pick<CreateExternalLinkRequest, "resourceId" | "spaceId" | "resourceType"> & {
  /** The item's CURRENT name; seeds the link label on create. */
  name: string;
  isFolder: boolean;
};

type CreateExternalLinkModalProps = {
  target: ExternalLinkTarget;
  /** Present when editing; absent when creating. */
  editingLink?: ExternalLinkListItem | null;
  onClose: () => void;
  /** Fired after a successful save so the caller can refresh its list / badge counts. */
  onSaved?: (link: ExternalLinkListItem) => void;
  /** Opens the Manage dialog from the confirmation panel. */
  onManage?: () => void;
};

/** Download implies View, so it is the more useful default and matches the product prototype. */
const DEFAULT_LINK_PERMISSION: ExternalLinkPermission = "download";

type FormState = {
  name: string;
  permission: ExternalLinkPermission;
  expiryEnabled: boolean;
  expiryDate: string;
  passwordEnabled: boolean;
  password: string;
};

function CreateExternalLinkModal({
  target,
  editingLink,
  onClose,
  onSaved,
  onManage,
}: Readonly<CreateExternalLinkModalProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isEditing = !!editingLink;
  /**
   * Reached from "Enable Link" on a revoked link the server refused to reinstate as-is: its expiry has
   * already passed, so the creator has to choose a new date here (PRD 3.4). It is a different intent
   * from an ordinary edit, so the labels and the submit path differ.
   */
  const isReinstating = !!editingLink?.revoked;

  const [form, setForm] = useState<FormState>(() => ({
    name: editingLink?.name ?? target.name,
    permission: editingLink?.permission ?? DEFAULT_LINK_PERMISSION,
    expiryEnabled: !!editingLink?.expiresAt,
    // A past date is cleared rather than pre-filled: the picker's `min` is tomorrow, so leaving the
    // stale date in the box would show a value the control itself rejects, and the only reason the
    // form is open is that this date needs replacing.
    expiryDate: isPastExpiry(editingLink?.expiresAt) ? "" : timestampToDateInput(editingLink?.expiresAt),
    // Editing never pre-fills the password — the server only ever returns `hasPassword`, and a
    // dummy value in the box would be saved verbatim as a new password on the next save.
    passwordEnabled: !!editingLink?.hasPassword,
    password: "",
  }));
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedLink, setSavedLink] = useState<ExternalLinkListItem | null>(null);
  const [copied, setCopied] = useState(false);
  const [revealSavedPassword, setRevealSavedPassword] = useState(false);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const expiryHint = useMemo(() => {
    const stamp = endOfDayTimestamp(form.expiryDate);
    if (!stamp) return "";
    // Says "the project's timezone" rather than naming a zone: the exact instant is resolved
    // server-side against the portal's zone, which this form does not know.
    return t("Expires at the end of {{date}}, in the project's timezone", {
      date: formatShortDate(stamp),
    });
  }, [form.expiryDate, t]);

  /**
   * Password is required when the toggle is on and the link does not already have one. When editing
   * a link that already has a password, leaving the box empty means "keep the existing password" —
   * so it must not block Save.
   */
  const passwordSatisfied =
    !form.passwordEnabled ||
    form.password.trim().length >= LINK_PASSWORD_MIN_LENGTH ||
    (isEditing && !!editingLink?.hasPassword);

  const canSave =
    !!form.name.trim() &&
    (!form.expiryEnabled || !!form.expiryDate) &&
    // The server refuses a reinstate with no future date, so Save stays inert until one is chosen
    // rather than round-tripping to be told the same thing.
    (!isReinstating || (form.expiryEnabled && !!form.expiryDate)) &&
    passwordSatisfied &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    if (form.expiryEnabled && !form.expiryDate) {
      dispatch(showToastNotification({ content: t("Please choose an expiry date"), type: TOAST_TYPE_WARNING }));
      return;
    }
    setSaving(true);
    try {
      // The bare date goes to the server, which resolves it to the end of that day in the PORTAL's
      // timezone. Resolving it here would use this browser's timezone instead.
      const expiresOn = form.expiryEnabled ? form.expiryDate : null;

      if (isEditing && editingLink) {
        /**
         * A REVOKED link reached here from "Enable Link" — the server refused to reinstate it as-is
         * because its expiry had already passed (PRD 3.4), and this form is where the new date is
         * chosen. Reinstate rather than update: `updateExternalLink` would set the new date and leave
         * the link revoked, so saving would appear to work and change nothing the recipient can see.
         *
         * The other fields are intentionally not sent on this path. The rule is that a link comes
         * back "with its original settings"; re-dating it is the one change being asked for, and
         * quietly applying whatever else the form happens to hold would exceed that.
         */
        if (editingLink.revoked) {
          const res = await reinstateExternalLink({ linkId: editingLink._id, expiresOn });
          if (!res.status) {
            dispatch(showToastNotification({ content: untranslated(res.errorMessage), type: TOAST_TYPE_ERROR }));
            return;
          }
          if (res.expiryRequired) {
            // Only reachable if the newly chosen date is itself already past, which the picker's
            // `min` normally prevents.
            dispatch(
              showToastNotification({
                content: t("Choose an expiry date in the future to enable this link"),
                type: TOAST_TYPE_WARNING,
              }),
            );
            return;
          }
          if (res.link) {
            setSavedLink(res.link);
            onSaved?.(res.link);
          }
          dispatch(showToastNotification({ content: t("Link enabled"), type: TOAST_TYPE_SUCCESS }));
          return;
        }

        // Built by PRESENCE, not by spreading: the server treats an omitted key as "leave alone"
        // and an explicit null as "clear". Always sending `password` would wipe the password of a
        // link whose password the user never touched.
        const payload: Parameters<typeof updateExternalLink>[0] = {
          linkId: editingLink._id,
          name: form.name.trim(),
          permission: form.permission,
          expiresOn,
        };
        if (!form.passwordEnabled) {
          payload.password = null;
        } else if (form.password.trim()) {
          payload.password = form.password.trim();
        }

        const res = await updateExternalLink(payload);
        if (!res.status) {
          dispatch(showToastNotification({ content: untranslated(res.errorMessage), type: TOAST_TYPE_ERROR }));
          return;
        }
        if (res.link) {
          setSavedLink(res.link);
          onSaved?.(res.link);
        }
        dispatch(showToastNotification({ content: t("Link updated"), type: TOAST_TYPE_SUCCESS }));
        return;
      }

      const res = await createExternalLink({
        resourceId: target.resourceId,
        spaceId: target.spaceId,
        resourceType: target.resourceType,
        name: form.name.trim(),
        permission: form.permission,
        expiresOn,
        password: form.passwordEnabled ? form.password.trim() : null,
      });
      if (!res.status) {
        dispatch(showToastNotification({ content: untranslated(res.errorMessage), type: TOAST_TYPE_ERROR }));
        return;
      }
      if (res.link) {
        setSavedLink(res.link);
        onSaved?.(res.link);
      }
      dispatch(showToastNotification({ content: t("Link created"), type: TOAST_TYPE_SUCCESS }));
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!savedLink) return;
    const ok = await copyToClipboard(savedLink.url);
    setCopied(ok);
    dispatch(
      showToastNotification({
        content: ok ? t("Link copied to clipboard") : t("Could not copy the link"),
        type: ok ? TOAST_TYPE_SUCCESS : TOAST_TYPE_ERROR,
      }),
    );
  };

  /**
   * Revert the button from "Copied" back to "Copy" (PRD 3.3: "confirms in place for roughly 1.5
   * seconds").
   *
   * Cleared on unmount and on re-copy, so closing the dialog mid-timer cannot set state on an
   * unmounted component and a second copy restarts the window rather than stacking timers.
   */
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPY_CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  // ── Form stage ────────────────────────────────────────────────────────────

  const permissionOption = (value: ExternalLinkPermission, icon: string, label: string, description: string) => {
    const selected = form.permission === value;
    return (
      <button
        type="button"
        onClick={() => setField("permission", value)}
        className={`flex flex-1 items-start gap-3 rounded-md border p-3 text-start transition ${
          selected
            ? "border-system_colour_1 bg-selection_primary"
            : "border-stroke_primary bg-white hover:bg-card_hover_color"
        }`}
        data-testid={`externalLinkPermission-${value}`}
      >
        <IconComponent name={icon} width="1.25rem" height="1.25rem" />
        <span className="min-w-0">
          <Text className="block text-sm font-semibold">{label}</Text>
          <Text className="mt-1 block text-xs text-text_light">{description}</Text>
        </span>
      </button>
    );
  };

  const formContent = (
    <div className="flex flex-col gap-5 py-2">
      {/* The item this link points at. Read-only context, not an editable field. */}
      <div className="flex items-center gap-2 rounded-md border border-stroke_primary bg-light_background px-3 py-2">
        <IconComponent name={target.isFolder ? "folder" : "files"} width="1rem" height="1rem" />
        <Text className="truncate text-sm text-text_light">{untranslated(target.name)}</Text>
      </div>

      <div className="flex flex-col gap-1">
        <Text className="text-sm font-medium">{t("Link name")}</Text>
        <TextFieldComponent
          value={form.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField("name", e.target.value)}
          placeholder={t("Enter a name for this link")}
          inputProps={{ maxLength: LINK_NAME_MAX_LENGTH }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Text className="text-sm font-medium">{t("Permission")}</Text>
        <div className="flex gap-3">
          {permissionOption("view", "EyeOpen", t("Can View"), t("Preview in the browser only"))}
          {permissionOption("download", "download", t("Can Download"), t("View and download originals"))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3 rounded-md border border-stroke_primary p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Text className="block text-sm font-semibold">{t("Set expiry")}</Text>
              <Text className="block text-xs text-text_light">{t("Link stops working after this date")}</Text>
            </div>
            <ToggleSwitchComponent
              checked={form.expiryEnabled}
              onChange={(_e: unknown, checked: boolean) => setField("expiryEnabled", checked)}
            />
          </div>
          {form.expiryEnabled && (
            <div className="flex flex-col gap-1">
              <TextFieldComponent
                type="date"
                value={form.expiryDate}
                inputProps={{ min: minExpiryDateInput() }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField("expiryDate", e.target.value)}
              />
              {!!expiryHint && <Text className="text-xs text-text_light">{expiryHint}</Text>}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 rounded-md border border-stroke_primary p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Text className="block text-sm font-semibold">{t("Set password")}</Text>
              <Text className="block text-xs text-text_light">{t("Recipients enter this before viewing")}</Text>
            </div>
            <ToggleSwitchComponent
              checked={form.passwordEnabled}
              onChange={(_e: unknown, checked: boolean) => setField("passwordEnabled", checked)}
            />
          </div>
          {form.passwordEnabled && (
            <div className="flex flex-col gap-1">
              <TextFieldComponent
                type={showPassword ? "text" : "password"}
                value={form.password}
                inputProps={{ maxLength: LINK_PASSWORD_MAX_LENGTH }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField("password", e.target.value)}
                placeholder={
                  isEditing && editingLink?.hasPassword
                    ? t("Leave blank to keep current password")
                    : t("Enter a password")
                }
                InputProps={{
                  endAdornment: (
                    <IconComponent
                      name={showPassword ? "EyeClose" : "EyeOpen"}
                      width="1rem"
                      height="1rem"
                      onClick={() => setShowPassword((v) => !v)}
                    />
                  ),
                }}
              />
              <Text className="text-xs text-text_light">
                {t("Minimum {{count}} characters", { count: LINK_PASSWORD_MIN_LENGTH })}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const formFooter = (
    <div className="flex w-full items-center gap-4">
      <Text className="flex-1 text-xs text-text_light">
        {form.passwordEnabled ? t("Recipients will need this password") : t("Anyone with the link can open it")}
      </Text>
      <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} disabled={!canSave} onClick={handleSave}>
        {isReinstating ? t("Enable Link") : isEditing ? t("Save changes") : t("Create link")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  // ── Confirmation stage ────────────────────────────────────────────────────

  const summaryCard = (label: string, value: string, extra?: React.ReactNode) => (
    <div className="rounded-md border border-stroke_primary px-3 py-2">
      <Text className="block text-xs text-text_light">{label}</Text>
      <div className="mt-1 flex items-center gap-2">
        <Text className="text-sm font-semibold">{value}</Text>
        {extra}
      </div>
    </div>
  );

  const renderDoneContent = (link: ExternalLinkListItem) => (
    <div className="flex flex-col gap-4 py-2">
      <div className="flex items-center gap-2 rounded-md bg-sla_on_track_bg px-3 py-2">
        <IconComponent name="TickRight" width="1rem" height="1rem" />
        <Text className="text-sm font-semibold">
          {isReinstating ? t("Link enabled") : isEditing ? t("Link updated") : t("Link created")}
        </Text>
      </div>

      <div className="flex flex-col gap-1">
        <Text className="text-sm font-medium">{t("Shareable URL")}</Text>
        <div className="flex items-stretch overflow-hidden rounded-md border border-stroke_primary">
          <div className="flex-1 truncate bg-light_background px-3 py-2 font-mono text-xs">
            {untranslated(link.url)}
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="border-s border-stroke_primary px-4 text-xs font-semibold hover:bg-card_hover_color"
            data-testid="copyExternalLinkUrl"
          >
            {copied ? t("Copied") : t("Copy")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {summaryCard(t("Permission"), link.permission === "download" ? t("Can Download") : t("Can View"))}
        {summaryCard(t("Expires"), link.expiresAt ? formatShortDate(link.expiresAt) : t("Never"))}
        {summaryCard(
          t("Password"),
          // The password is never echoed back by the server. When one was just typed in this
          // session it can be revealed for the creator to copy; on an edit that kept the existing
          // password there is nothing to show but the fact that one is set.
          link.hasPassword ? (revealSavedPassword && form.password ? form.password : "••••••••") : t("None"),
          link.hasPassword && form.password ? (
            <IconComponent
              name={revealSavedPassword ? "EyeClose" : "EyeOpen"}
              width="0.875rem"
              height="0.875rem"
              onClick={() => setRevealSavedPassword((v) => !v)}
            />
          ) : undefined,
        )}
      </div>

      <Text className="text-xs leading-relaxed text-text_light">
        {t(
          "Share the link and the password through separate channels. Anyone holding both can access this content until it expires or is revoked.",
        )}
      </Text>
    </div>
  );

  const doneFooter = (
    <div className="flex w-full items-center gap-3">
      {/* PRD 3.3: opens the recipient view "in a new context". A new tab, not this one — the creator
          is mid-flow in a dialog, and navigating away would lose it. `noopener` because the share
          origin must not get a handle on this window. */}
      <ButtonComponent
        buttonType={BUTTON_TYPES.BORDER_BUTTON}
        onClick={() => savedLink && globalThis.open(savedLink.url, "_blank", "noopener,noreferrer")}
        data-testid="openExternalLinkAsRecipient"
      >
        <span className="flex items-center gap-2">
          <IconComponent name="openInViewer" width="0.875rem" height="0.875rem" />
          <Text>{t("Open as recipient")}</Text>
        </span>
      </ButtonComponent>
      <div className="flex-1" />
      {!!onManage && (
        <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onManage}>
          {t("Manage links")}
        </ButtonComponent>
      )}
      <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={onClose}>
        {t("Done")}
      </ButtonComponent>
    </div>
  );

  const title = savedLink
    ? isReinstating
      ? t("Link enabled")
      : isEditing
        ? t("Link updated")
        : t("Link created")
    : isReinstating
      ? t("Enable external link")
      : isEditing
        ? t("Edit external link")
        : t("Create external link");

  return (
    <ModalStateDialogComponent
      isVisible
      width={LINK_DIALOG_WIDTH}
      headerTitle={title}
      onCancel={onClose}
      modelContent={savedLink ? renderDoneContent(savedLink) : formContent}
      footerContent={savedLink ? doneFooter : formFooter}
    />
  );
}

export default CreateExternalLinkModal;
export type { ExternalLinkTarget };
